import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import Image from "next/image";
import Footer from "@/components/footer/Footer";
import Header from "@/components/header/Header";
import { DynamicCategorySideBar, DynamicMobileCategorySidebar } from '@/components/dynamic-imports';
import { getAllProductCategories, getSubcategoriesAndProducts } from '@/utils/woocommerce';
import { trackViewItemList } from '@/utils/tracking';

const DynamicProductsPage = dynamic(() => import('@/components/products/ProductsPage.js'), {
  loading: () => <div>Loading products...</div>,
});

const ProductCategoryPage = ({ subcategoriesWithProducts, categoryData, targetCategoryId }) => {
  const paddingClass = `products-background ${categoryData?.image?.src ? "pt-[5rem] md:pt-36 pb-18" : "py-36"}`;

  const [loadingCategory, setLoadingCategory] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState(subcategoriesWithProducts[0]?.subcategory?.id || null);

  const categoryRefs = useRef([]);
  const scrollTimeoutRef = useRef(null); // Ref to hold the timeout ID for scroll throttling

  // Load the next batch of categories
  const loadNextCategory = useCallback(() => {
    if (currentCategoryIndex < subcategoriesWithProducts.length - 1) {
      setCurrentCategoryIndex((prevIndex) => prevIndex + 1);
    }
  }, [currentCategoryIndex, subcategoriesWithProducts]);

  // Scroll to a specific category
  const scrollToCategory = useCallback((categoryId) => {
    const categoryIndex = subcategoriesWithProducts.findIndex(sub => sub.subcategory.id === categoryId);

    if (categoryRefs.current[categoryIndex]) {
      categoryRefs.current[categoryIndex].scrollIntoView({ behavior: 'smooth' });
    } else {
      setLoadingCategory(categoryId);
    }
  }, [subcategoriesWithProducts]);

  useEffect(() => {
    if (targetCategoryId) {
      // If there is a target child category, scroll to it after rendering
      scrollToCategory(targetCategoryId);
    }
  }, [targetCategoryId, scrollToCategory]);

  // Recursive function to load more categories and scroll until the target category is visible
  const loadUntilCategoryVisible = useCallback(() => {
    const categoryIndex = subcategoriesWithProducts.findIndex(sub => sub.subcategory.id === loadingCategory);
    if (categoryRefs.current[categoryIndex]) {
      categoryRefs.current[categoryIndex].scrollIntoView({ behavior: 'smooth' });
      setLoadingCategory(null);
    } else {
      if (!isScrolling) {
        setIsScrolling(true);
        setTimeout(() => {
          loadNextCategory();
          setIsScrolling(false);
        }, 500); // Reduced delay for a faster response
      }
    }
  }, [loadingCategory, subcategoriesWithProducts, isScrolling, loadNextCategory]);

  // IntersectionObserver to detect which category is in view
  useEffect(() => {
    const currentRefs = categoryRefs.current; // Store the current ref values

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const visibleCategoryId = entry.target.getAttribute('data-category-id');
            setActiveCategoryId(visibleCategoryId);
          }
        });
      },
      { threshold: 0.1 } // Adjust the threshold as needed
    );

    currentRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [currentCategoryIndex]);

  // Throttle scroll event manually
  useEffect(() => {
    const handleScroll = () => {
      if (scrollTimeoutRef.current) return;

      scrollTimeoutRef.current = setTimeout(() => {
        scrollTimeoutRef.current = null;

        const categoryInView = categoryRefs.current.find((ref) => {
          const rect = ref.getBoundingClientRect();
          return rect.top >= 0 && rect.bottom <= window.innerHeight;
        });

        if (categoryInView) {
          const visibleCategoryId = categoryInView.getAttribute('data-category-id');
          setActiveCategoryId(visibleCategoryId);
        }
      }, 1000); // Throttle scroll event every 200ms
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (loadingCategory) {
      loadUntilCategoryVisible();
    }
  }, [loadingCategory, currentCategoryIndex, loadUntilCategoryVisible]);

  useEffect(() => {
    // Track view_item_list event when the category page loads
    const allProducts = subcategoriesWithProducts.flatMap(sub => sub.fetchedProducts);
    trackViewItemList(allProducts);
  }, [subcategoriesWithProducts]);

  return (
    <>
      <Header title={categoryData.name} />
      <main className={paddingClass}>
        {categoryData?.image?.src && (
          <div className="relative w-full h-[10rem] md:h-[20rem] lg:h-[18.75rem] overflow-hidden">
            <Image src={categoryData.image.src} alt={categoryData.image.alt || categoryData.name} style={{ objectFit: "cover" }} fill />
          </div>
        )}
        <div className="2xl:container mx-auto min-h-screen md:py-8">
          <div className="flex flex-col md:flex-row">
            {/* Mobile Category Sidebar */}
            <DynamicMobileCategorySidebar
              subcategories={subcategoriesWithProducts.map(sub => sub.subcategory)}
              onCategorySelect={scrollToCategory}
              activeCategoryId={activeCategoryId}
            />

            {/* Desktop Category Sidebar */}
            <div className="hidden md:block w-[17%] h-screen sticky top-36 pe-0 sm:pe-6 md:pe-2 lg:pe-5 2xl:pe-14">
              <h1 className="mb-2 text-3xl text-balance text-heading text-bold font-secondary text-primary-studio">
                {categoryData.name}
              </h1>
              <hr className="dotted-spaced my-4" />
              <DynamicCategorySideBar
                subcategories={subcategoriesWithProducts.map(sub => sub.subcategory)}
                onCategorySelect={scrollToCategory}
                activeCategoryId={activeCategoryId}
              />
            </div>
            <div className="w-full md:w-[83%] mt-4 md:mt-0 px-4 2xl:px-0">
              {subcategoriesWithProducts.slice(0, currentCategoryIndex + 1).map(({ subcategory, fetchedProducts, fetchedCrossSellProducts }, index) => (
                <div
                  key={subcategory.id}
                  className="w-full mb-0 md:mb-12"
                  ref={el => categoryRefs.current[index] = el}
                  data-category-id={subcategory.id}
                  style={{ scrollMarginTop: "9rem" }}
                >
                  <h2 className="text-2xl md:text-4xl font-secondary text-bold mb-2 md:mb-4 text-primary-studio">{subcategory.name}</h2>
                  <DynamicProductsPage
                    initialProducts={fetchedProducts}
                    crossSellProducts={fetchedCrossSellProducts}
                    categoryId={subcategory.id}
                    loadNextCategory={loadNextCategory}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};
ProductCategoryPage.propTypes = {
  subcategoriesWithProducts: PropTypes.arrayOf(
    PropTypes.shape({
      subcategory: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
      fetchedProducts: PropTypes.array.isRequired,
      fetchedCrossSellProducts: PropTypes.array.isRequired,
    })
  ).isRequired,
  categoryData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    image: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
  }).isRequired,
  targetCategoryId: PropTypes.number,
};

export default ProductCategoryPage;

export async function getStaticProps({ params }) {
  const slugArray = params.slug;
  const categoriesData = await getAllProductCategories();

  function findCategoryBySlugPath(categories, slugPath) {
    let currentCategories = categories.filter(cat => cat.parent === 0); // Root categories
    let matchedCategory = null;

    for (const slugSegment of slugPath) {
      matchedCategory = currentCategories.find(cat => decodeURIComponent(cat.slug) === slugSegment);
      if (!matchedCategory) {
        return null; // Category not found
      }
      currentCategories = categories.filter(cat => cat.parent === matchedCategory.id);
    }

    return matchedCategory;
  }

  const category = findCategoryBySlugPath(categoriesData, slugArray);

  if (!category) {
    return { notFound: true }; // 404 if category not found
  }

  // If the category is a child, find the parent category.
  const parentCategory = category.parent === 0 ? category : categoriesData.find(cat => cat.id === category.parent);

  // Fetch subcategories and products based on the parent category
  const subcategoriesWithProducts = await getSubcategoriesAndProducts(parentCategory.id, 10);

  // Include the target category ID to scroll to (only relevant for child categories)
  const targetCategoryId = category.parent !== 0 ? category.id : null;

  const settingsRes = await fetch(`${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver_next_wordpress/v1/settings`);
  const studioRes = await fetch(`${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver_next_wordpress/v1/acf_studio`);

  const data = await settingsRes.json();
  const studioData = await studioRes.json();

  let lang = 'en';
  if (data?.language) {
    lang = data.language.split('_')[0];
  }

  const messages = await import(`../../messages/${lang}.json`);

  return {
    props: {
      data: data || {},
      studioData: studioData || {},
      messages: messages.default,
      categoryData: parentCategory, // We use the parent category data
      subcategoriesWithProducts,
      targetCategoryId, // Pass this to handle scrolling to the child category
    },
    revalidate: 1,
  };
}

export async function getStaticPaths() {
  const categoriesData = await getAllProductCategories();

  // Function to recursively build paths
  function buildCategoryPaths(categories, parent = 0, parentSlugs = []) {
    const paths = [];
    const childCategories = categories.filter(cat => cat.parent === parent);

    childCategories.forEach(cat => {
      const slugPath = [...parentSlugs, decodeURIComponent(cat.slug)];
      paths.push({ params: { slug: slugPath } });
      paths.push(...buildCategoryPaths(categories, cat.id, slugPath));
    });

    return paths;
  }

  const paths = buildCategoryPaths(categoriesData);

  return {
    paths,
    fallback: 'blocking',
  };
}
